import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AppUtils } from '../../../core/config/app.utils';
import { SETTINGS } from '../../../core/config/common.settings';
import { RegistrationFormService } from '../services/registration-form.service';
import { Router } from '@angular/router';
import { LoadingService } from '../../../core/services/loading.service';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { CountryService } from '../../../core/services/country.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
})
export class RegistrationFormComponent implements OnInit {
  registrationForm: any;
  formErrors: any = {};
  message: any = {
    status: null,
    message: null,
  };
  countries: any[] = [];
  confirmPasswordVisible = true;
  passwordVisible = true;
  actionMessage = new ActionAlert();
  msg = false;

  constructor(
    private fb: FormBuilder,
    private registrationFormService: RegistrationFormService,
    private router: Router,
    private loadingService: LoadingService,
    private countryService: CountryService,
    private authService: AuthService,
  ) {
    this.formErrors = {
      companyName: {},
      firstname: {},
      lastname: {},
      shortCode: {},
      email: {},
      mobile: {},
      dialCode: {},
      password: {},
    };
  }

  ngOnInit(): void {
    Promise.all([this.getCountries()]).then();
    this.registrationForm = this.fb.group({
      companyName: [null, Validators.required],
      firstname: [null, Validators.required],
      shortCode: [
        '',
        [
          Validators.compose([
            Validators.required,
            Validators.maxLength(3),
            Validators.pattern(SETTINGS.REGEX.ONLY_UPPERCASE_LETTERS.EXP),
          ]),
        ],
      ],
      lastname: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      mobile: [
        null,
        [Validators.required, Validators.pattern(SETTINGS.REGEX.PHONE.EXP)],
      ],
      dialCode: [null, Validators.required],
      password: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.PASSWORD.EXP),
        ]),
      ],
    });
    this.registrationForm.valueChanges.subscribe((form: any) => {
      this.formErrors = AppUtils.getFormErrors(
        this.registrationForm,
        this.formErrors
      );
    });

    this.registrationForm
      .get('companyName')
      .valueChanges.subscribe((value: string) => {
        if (value) {
          const shortCode = this.generateShortCode(value);
          this.registrationForm.get('shortCode').setValue(shortCode);
        }
      });
  }

  generateShortCode(companyName: string): string {
    const words = companyName
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0);
    let shortCode = '';

    if (words.length >= 3) {
      shortCode = words
        .slice(0, 3)
        .map(word => word[0])
        .join('');
    } else if (words.length === 2) {
      shortCode = words.map(word => word[0]).join('');
      const randomChar = String.fromCharCode(
        65 + Math.floor(Math.random() * 26)
      );
      shortCode += randomChar;
    } else if (words.length === 1) {
      const word = words[0];
      if (word.length >= 3) {
        shortCode = word.substring(0, 3);
      } else {
        shortCode = word;
        while (shortCode.length < 3) {
          shortCode += String.fromCharCode(65 + Math.floor(Math.random() * 26));
        }
      }
    }

    return shortCode.toUpperCase();
  }

  confirmationValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !== this.registrationForm.controls.password.value
    ) {
      return { confirm: true, error: true };
    }
    return {};
  };

  async getCountries(): Promise<void> {
    this.countries = await this.countryService.getCountries();
  }

  async onRegistration(): Promise<void> {
    this.loadingService.start();
    const values = this.registrationForm.getRawValue();
    const payload = {
      companyName: values.companyName,
      shortCode: values.shortCode,
      dialCode: values.dialCode,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      mobile: values.mobile,
      password: values.password,
      selectedLanguages: ['en'],
    };
    try {
      await this.registrationFormService.registration(payload);
      this.registrationForm.reset();
      this.loadingService.stop();
      await this.authService.login(payload.email, payload.password);
    } catch (e) {
      this.loadingService.stop();
    }
  }

  shortCodeAlertError(): void {
    this.msg = true;
    this.actionMessage = new ActionAlert(
      true,
      true,
      'Short Code needs to be unique. Please enter a different Short Code',
      'error'
    );
  }

  orderByName(list: any[]): any {
    return AppUtils.sortOrderByName(list);
  }
}
